<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import de from '../../../../lang/de.yml'
import en from '../../../../lang/en.yml'

const msgs = { de: de.de, en: en.en }

const props = defineProps({
    items:  { type: Array,  default: () => [] },
    locale: { type: String, default: 'de_DE' },
})

function t(key) {
    const lang = msgs[props.locale.substring(0, 2)] ?? msgs.de
    return key.split('.').reduce((o, k) => o?.[k], lang) ?? key
}

const PER_PAGE = 10

const filter         = ref('')
const currentPage    = ref(1)
const collapsed      = ref({})
const showBtn        = ref({})
const textRefs       = {}
const collapsedHeights = {}

// Start all items collapsed so overflow can be measured after mount
props.items.forEach(item => { collapsed.value[item.ID] = true })

const showFilter = computed(() =>
    props.items.some(i => i.Type === 'news') && props.items.some(i => i.Type === 'event')
)

const filtered = computed(() =>
    filter.value ? props.items.filter(i => i.Type === filter.value) : props.items
)

const totalPages = computed(() =>
    Math.max(1, Math.ceil(filtered.value.length / PER_PAGE))
)

const paged = computed(() => {
    const start = (currentPage.value - 1) * PER_PAGE
    return filtered.value.slice(start, start + PER_PAGE)
})

const pages = computed(() => {
    const n   = totalPages.value
    const cur = currentPage.value
    if (n <= 7) return Array.from({ length: n }, (_, i) => i + 1)
    const set = new Set([1, n, cur - 1, cur, cur + 1].filter(p => p >= 1 && p <= n))
    return [...set].sort((a, b) => a - b)
})

watch(filter, () => { currentPage.value = 1 })

function setTextRef(el, id) {
    if (el) textRefs[id] = el
    else delete textRefs[id]
}

function closeEl(el, toH, done) {
    el.style.overflow = 'hidden'
    el.style.height = el.scrollHeight + 'px'
    el.offsetHeight
    el.style.height = toH + 'px'
    el.addEventListener('transitionend', () => { el.style.height = ''; el.style.overflow = ''; done?.() }, { once: true })
}

function toggle(id) {
    const el = textRefs[id]
    const collapsedH = collapsedHeights[id]
    if (!el || !collapsedH) { collapsed.value[id] = !collapsed.value[id]; return }

    if (collapsed.value[id]) {
        el.style.overflow = 'hidden'
        el.style.height = collapsedH + 'px'
        collapsed.value[id] = false
        nextTick(() => {
            el.offsetHeight
            el.style.height = el.scrollHeight + 'px'
            el.addEventListener('transitionend', () => { el.style.height = ''; el.style.overflow = '' }, { once: true })
        })
    } else {
        closeEl(el, collapsedH, () => { collapsed.value[id] = true })
    }
}

async function measure() {
    await nextTick()
    paged.value.forEach(item => {
        const el = textRefs[item.ID]
        if (!el) return
        const overflows = el.scrollHeight > el.clientHeight + 1
        if (overflows) collapsedHeights[item.ID] = el.clientHeight
        collapsed.value[item.ID] = overflows
        showBtn.value[item.ID]   = overflows
    })
}

onMounted(measure)
watch(paged, measure, { flush: 'post' })
</script>

<template>
    <div>
        <div v-if="showFilter" class="NewsList__Toolbar">
            <select v-model="filter" class="NewsList__Filter">
                <option value="">{{ t('NEWSENTRY.type_all') }}</option>
                <option value="news">{{ t('NEWSENTRY.type_news') }}</option>
                <option value="event">{{ t('NEWSENTRY.type_event') }}</option>
            </select>
        </div>

        <div v-for="item in paged" :key="item.ID" class="NewsItem">
            <div class="NewsItem__Image" v-html="item.ImageHTML"></div>
            <div class="NewsItem__Content">
                <div class="NewsItem__Topline">
                    <div :class="`NewsItem__Topline--type type--${item.Type}`">{{ item.TypeLabel }}</div>
                    <div class="NewsItem__Topline--date" v-html="item.StartDateFormatted"></div>
                </div>
                <h2 class="NewsItem__Title">{{ item.Title }}</h2>
                <div class="NewsItem__Body">
                    <div
                        :ref="el => setTextRef(el, item.ID)"
                        :class="['NewsItem__Body--text', { 'is-collapsed': collapsed[item.ID] }]"
                        v-html="item.Content"
                    ></div>
                    <button
                        v-if="showBtn[item.ID]"
                        type="button"
                        class="link__button link__button--accordion is-visible"
                        :aria-expanded="String(!collapsed[item.ID])"
                        @click="toggle(item.ID)"
                    >{{ collapsed[item.ID] ? t('NEWSENTRY.read_more') : t('NEWSENTRY.read_less') }}</button>
                </div>
            </div>
        </div>

        <nav v-if="totalPages > 1" class="NewsList__Pagination" :aria-label="t('NEWSLIST.pagination_nav')">
            <button
                class="NewsList__Pagination--btn"
                :disabled="currentPage === 1"
                :aria-label="t('NEWSLIST.prev_page')"
                @click="currentPage--"
            >«</button>

            <template v-for="(page, i) in pages" :key="page">
                <span
                    v-if="i > 0 && page - pages[i - 1] > 1"
                    class="NewsList__Pagination--ellipsis"
                    aria-hidden="true"
                >…</span>
                <button
                    :class="['NewsList__Pagination--btn', { 'is-active': page === currentPage }]"
                    :aria-current="page === currentPage ? 'page' : undefined"
                    @click="currentPage = page"
                >{{ page }}</button>
            </template>

            <button
                class="NewsList__Pagination--btn"
                :disabled="currentPage === totalPages"
                :aria-label="t('NEWSLIST.next_page')"
                @click="currentPage++"
            >»</button>
        </nav>
    </div>
</template>
